import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Globe,
  Search,
  BarChart3,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Zap,
  Rss,
  FileCode2,
  Copy,
  Check,
  HelpCircle,
  TrendingUp,
  Smartphone,
  Monitor,
  Tag,
  FileText,
  Image as ImageIcon,
  User,
  Eye,
  Info,
} from 'lucide-react';

interface SEOIntegrationsManagerProps {
  primaryColor?: string;
}

export default function SEOIntegrationsManager({ primaryColor = '#059669' }: SEOIntegrationsManagerProps) {
  const { settings, updateSettingsOnServer, showToast } = useApp();

  // State for SEO Metadata (Google Search Title & Description)
  const [seoData, setSeoData] = useState(() => ({
    metaTitle: settings?.seo?.metaTitle || (settings?.tagline && settings?.siteName ? `${settings.siteName} - ${settings.tagline}` : (settings?.siteName || '')),
    metaDescription: settings?.seo?.metaDescription || settings?.tagline || '',
    metaKeywords: settings?.seo?.metaKeywords || '',
    ogImage: settings?.seo?.ogImage || settings?.logoUrl || '',
    author: settings?.seo?.author || settings?.siteName || 'Administrator',
    canonicalUrl: (settings?.seo?.canonicalUrl && !settings.seo.canonicalUrl.includes('example.com')) ? settings.seo.canonicalUrl : (typeof window !== 'undefined' ? window.location.origin : 'https://bellvatour.com'),
  }));

  // State for Google Integrations
  const [integrations, setIntegrations] = useState(() => ({
    googleSearchConsole: settings?.integrations?.googleSearchConsole || '',
    googleAnalytics: settings?.integrations?.googleAnalytics || '',
    googleAdsense: settings?.integrations?.googleAdsense || '',
  }));

  // SERP Preview Mode (Desktop / Mobile)
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  useEffect(() => {
    if (settings) {
      setSeoData({
        metaTitle: settings.seo?.metaTitle || (settings.tagline && settings.siteName ? `${settings.siteName} - ${settings.tagline}` : (settings.siteName || '')),
        metaDescription: settings.seo?.metaDescription || settings.tagline || '',
        metaKeywords: settings.seo?.metaKeywords || '',
        ogImage: settings.seo?.ogImage || settings.logoUrl || '',
        author: settings.seo?.author || settings.siteName || 'Administrator',
        canonicalUrl: (settings.seo?.canonicalUrl && !settings.seo.canonicalUrl.includes('example.com')) ? settings.seo.canonicalUrl : (typeof window !== 'undefined' ? window.location.origin : 'https://bellvatour.com'),
      });
      setIntegrations({
        googleSearchConsole: settings.integrations?.googleSearchConsole || '',
        googleAnalytics: settings.integrations?.googleAnalytics || '',
        googleAdsense: settings.integrations?.googleAdsense || '',
      });
    }
  }, [settings]);

  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);
  const [isPinging, setIsPinging] = useState(false);
  const [pingResult, setPingResult] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateSettingsOnServer({
        integrations,
        seo: {
          metaTitle: seoData.metaTitle.trim(),
          metaDescription: seoData.metaDescription.trim(),
          metaKeywords: seoData.metaKeywords.trim(),
          author: seoData.author.trim(),
          ogImage: seoData.ogImage.trim(),
          canonicalUrl: seoData.canonicalUrl.trim() || (typeof window !== 'undefined' ? window.location.origin : 'https://bellvatour.com'),
        },
      });
      const timeStr = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setLastSavedTime(timeStr);
      showToast('Pengaturan SEO Google & Kode Integrasi berhasil disimpan!', 'success');
    } catch (err) {
      showToast('Gagal menyimpan pengaturan SEO', 'error');
    }
    setIsSaving(false);
  };

  const handlePingGoogle = async () => {
    setIsPinging(true);
    setPingResult(null);
    try {
      const res = await fetch('/api/seo/ping', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setPingResult('Pemberitahuan Sitemap berhasil dikirim ke Google & Bing Indexer! Bot akan segera merayapi artikel baru Anda.');
        showToast('Ping sitemap ke mesin pencari sukses!', 'success');
      } else {
        setPingResult('Ping selesai dijalankan.');
      }
    } catch (err) {
      setPingResult('Ping terkirim ke antrean crawler.');
    }
    setIsPinging(false);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(label);
    showToast(`Link ${label} disalin ke clipboard!`, 'info');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const originUrl = typeof window !== 'undefined' ? window.location.origin : 'https://bellvatour.com';
  const siteDomain = seoData.canonicalUrl
    ? seoData.canonicalUrl.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
    : originUrl.replace(/^https?:\/\//i, '');

  const displayTitle = seoData.metaTitle.trim() || (settings?.siteName ? `${settings.siteName} - ${settings.tagline || 'Website Resmi'}` : 'Website Resmi');
  const displayDescription = seoData.metaDescription.trim() || (settings?.tagline ? `${settings.siteName || 'Website'} - ${settings.tagline}. Solusi terpadu dan profesional untuk kebutuhan Anda.` : 'Website resmi menyediakan informasi lengkap dan layanan profesional terpercaya.');

  // Description character count metrics
  const descLen = seoData.metaDescription.length;
  const isDescIdeal = descLen >= 120 && descLen <= 160;
  const isDescTooShort = descLen > 0 && descLen < 120;
  const isDescTooLong = descLen > 160;

  // Title character count metrics
  const titleLen = seoData.metaTitle.length;
  const isTitleIdeal = titleLen >= 40 && titleLen <= 65;

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600" />
            <span>Pengaturan SEO Google & Integrasi Webmaster</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Atur judul & meta deskripsi yang muncul di hasil pencarian Google, pasang verifikasi Google Search Console, Google Analytics, dan AdSense.
          </p>
        </div>

        <button
          id="btn-save-seo-top"
          disabled={isSaving}
          onClick={handleSave}
          className="px-5 py-2.5 rounded-xl text-white font-bold text-xs shadow-md transition-transform hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-2 shrink-0"
          style={{ backgroundColor: primaryColor }}
        >
          {isSaving ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Menyimpan...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span>Simpan Perubahan SEO</span>
            </>
          )}
        </button>
      </div>

      {lastSavedTime && (
        <div
          id="seo-integrations-saved-notice"
          className="p-3 bg-emerald-50 border border-emerald-300 rounded-2xl text-emerald-900 text-xs font-bold flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span>Pengaturan Judul, Meta Deskripsi & Integrasi Google berhasil disimpan ke database.</span>
          </div>
          <span className="text-[10px] text-emerald-700 font-mono bg-emerald-100 px-2 py-0.5 rounded-lg">
            Terakhir disimpan: {lastSavedTime}
          </span>
        </div>
      )}

      {/* SECTION 1: GOOGLE SEARCH ENGINE RESULTS PAGE (SERP) PREVIEW & SETTINGS */}
      <div className="p-5 sm:p-6 rounded-3xl border border-slate-200 bg-white shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <Search className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <span>1. Tampilan Website di Mesin Pencari Google</span>
                <span className="bg-blue-100 text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                  Google SERP
                </span>
              </h3>
              <p className="text-xs text-slate-500">
                Judul dan deskripsi berikut adalah yang akan dibaca oleh robot Google dan ditampilkan kepada calon pengunjung.
              </p>
            </div>
          </div>

          {/* Desktop / Mobile Toggle for SERP Preview */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl text-xs font-semibold text-slate-600">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-lg transition-colors ${
                previewMode === 'desktop' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'hover:text-slate-900'
              }`}
            >
              <Monitor className="w-3.5 h-3.5" />
              <span>Desktop</span>
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-lg transition-colors ${
                previewMode === 'mobile' ? 'bg-white text-slate-900 shadow-2xs font-bold' : 'hover:text-slate-900'
              }`}
            >
              <Smartphone className="w-3.5 h-3.5" />
              <span>Mobile</span>
            </button>
          </div>
        </div>

        {/* LIVE GOOGLE SERP PREVIEW BOX */}
        <div className="p-4 sm:p-5 rounded-2xl bg-slate-50/80 border border-slate-200/80 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5 text-blue-600" />
              <span>Pratinjau Hasil Pencarian Google ({previewMode === 'desktop' ? 'Tampilan Komputer' : 'Tampilan Smartphone'}):</span>
            </span>
            <span className="text-[10px] text-slate-400">Simulasi Real-Time</span>
          </div>

          {/* Google Result Card */}
          <div
            className={`p-4 bg-white rounded-2xl border border-slate-200/90 shadow-2xs transition-all ${
              previewMode === 'mobile' ? 'max-w-md mx-auto' : 'w-full'
            }`}
          >
            {/* Google Header / Favicon + Domain */}
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                {settings?.faviconUrl || settings?.logoUrl ? (
                  <img
                    src={settings.faviconUrl || settings.logoUrl}
                    alt="Favicon"
                    className="w-4 h-4 object-contain"
                    onError={(e) => {
                      (e.target as HTMLElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <Globe className="w-3.5 h-3.5 text-slate-400" />
                )}
              </div>
              <div className="leading-tight overflow-hidden">
                <span className="text-xs font-medium text-slate-900 block truncate">
                  {settings?.siteName || 'Website Anda'}
                </span>
                <span className="text-[11px] text-[#202124] block truncate font-mono">
                  https://{siteDomain}
                </span>
              </div>
            </div>

            {/* Google SERP Title (Blue clickable link) */}
            <h4 className="text-[16px] sm:text-[18px] font-medium text-[#1a0dab] hover:underline cursor-pointer leading-snug line-clamp-1">
              {displayTitle}
            </h4>

            {/* Google SERP Snippet / Description */}
            <p className="text-[13px] sm:text-[14px] text-[#4d5156] leading-relaxed line-clamp-2 mt-1">
              {displayDescription}
            </p>
          </div>
        </div>

        {/* INPUT FIELDS FOR META TITLE & DESCRIPTION */}
        <div className="grid grid-cols-1 gap-5">
          {/* 1. Meta Title */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-blue-600" />
                <span>Judul Website di Google Search (Meta Title):</span>
              </label>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[11px] font-mono px-2 py-0.5 rounded-md font-bold ${
                    isTitleIdeal
                      ? 'bg-emerald-100 text-emerald-800'
                      : titleLen > 65
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {titleLen} / 60 Karakter {isTitleIdeal && '✓ Ideal'}
                </span>
              </div>
            </div>
            <input
              type="text"
              placeholder="Contoh: Nama Perusahaan Anda - Layanan Profesional & Solusi Terbaik"
              value={seoData.metaTitle}
              onChange={(e) => setSeoData({ ...seoData, metaTitle: e.target.value })}
              className="w-full px-4 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              💡 <strong>Rekomendasi Google:</strong> Gunakan antara <strong>50 – 60 karakter</strong>. Masukkan nama brand utama dan kata kunci produk terpopuler Anda.
            </p>
          </div>

          {/* 2. Meta Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <FileCode2 className="w-3.5 h-3.5 text-emerald-600" />
                <span>Meta Deskripsi Website di Google Search (Meta Description):</span>
              </label>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[11px] font-mono px-2 py-0.5 rounded-md font-bold ${
                    isDescIdeal
                      ? 'bg-emerald-100 text-emerald-800'
                      : isDescTooLong
                      ? 'bg-rose-100 text-rose-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {descLen} / 160 Karakter {isDescIdeal ? '✓ Sangat Ideal' : isDescTooShort ? '⚠️ Terlalu Pendek' : '⚠️ Terlalu Panjang'}
                </span>
              </div>
            </div>
            <textarea
              rows={3}
              placeholder="Contoh: Perusahaan penyedia layanan profesional, produk berkualitas tinggi, dan solusi terpercaya untuk kebutuhan bisnis Anda. Dapatkan penawaran terbaik sekarang!"
              value={seoData.metaDescription}
              onChange={(e) => setSeoData({ ...seoData, metaDescription: e.target.value })}
              className="w-full px-4 py-2.5 text-xs sm:text-sm bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 focus:outline-none leading-relaxed"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              💡 <strong>Rekomendasi Google:</strong> Gunakan antara <strong>120 – 160 karakter</strong>. Jelaskan rangkuman layanan unggulan Anda dan akhiri dengan ajakan bertindak (Call to Action).
            </p>
          </div>

          {/* 3. Meta Keywords & Author & OG Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {/* Keywords */}
            <div>
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5 mb-1.5">
                <Tag className="w-3.5 h-3.5 text-amber-600" />
                <span>Kata Kunci Pencarian (Meta Keywords):</span>
              </label>
              <input
                type="text"
                placeholder="outbound bandung, gathering kantor, eo gathering, rafting lembang"
                value={seoData.metaKeywords}
                onChange={(e) => setSeoData({ ...seoData, metaKeywords: e.target.value })}
                className="w-full px-3.5 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-amber-500 focus:outline-none"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Pisahkan beberapa kata kunci menggunakan tanda koma (,).
              </span>
            </div>

            {/* Author */}
            <div>
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5 mb-1.5">
                <User className="w-3.5 h-3.5 text-indigo-600" />
                <span>Nama Penulis / Publisher Default (Author):</span>
              </label>
              <input
                type="text"
                placeholder="Bellva Adventure Team"
                value={seoData.author}
                onChange={(e) => setSeoData({ ...seoData, author: e.target.value })}
                className="w-full px-3.5 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
              <span className="text-[10px] text-slate-400 mt-1 block">
                Ditampilkan pada schema Rich Snippet penulisan artikel di Google.
              </span>
            </div>

            {/* OG Image / Social Thumbnail */}
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5 mb-1.5">
                <ImageIcon className="w-3.5 h-3.5 text-rose-600" />
                <span>Gambar Thumbnail Berbagi Media Sosial & Google Discover (OG Image URL):</span>
              </label>
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  placeholder="https://domainanda.com/logo-share.jpg"
                  value={seoData.ogImage}
                  onChange={(e) => setSeoData({ ...seoData, ogImage: e.target.value })}
                  className="w-full px-3.5 py-2 text-xs bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-rose-500 focus:outline-none"
                />
                {seoData.ogImage && (
                  <div className="w-10 h-10 rounded-xl border border-slate-200 overflow-hidden shrink-0 bg-slate-100 flex items-center justify-center">
                    <img
                      src={seoData.ogImage}
                      alt="OG Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
              <span className="text-[10px] text-slate-400 mt-1 block">
                Thumbnail yang muncul saat tautan beranda website dibagikan ke WhatsApp, Facebook, LinkedIn, dan Telegram.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2: DOMAIN CANONICAL & SITEMAP SETUP */}
      <div className="p-5 sm:p-6 rounded-3xl border border-emerald-200 bg-emerald-50/40 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl bg-emerald-600 text-white flex items-center justify-center font-bold">
              <Globe className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">2. Domain Resmi & Canonical URL Website</h3>
              <span className="text-[11px] text-slate-500">Domain dasar untuk pembuatan Sitemap XML dan verifikasi kepemilikan Google</span>
            </div>
          </div>
          <span className="bg-emerald-600 text-white text-[10px] font-bold px-3 py-1 rounded-full">
            {siteDomain}
          </span>
        </div>

        <div>
          <label className="text-xs font-bold text-slate-700 block mb-1">
            Canonical Domain / URL Resmi Website:
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="https://bellvatour.com"
              value={seoData.canonicalUrl}
              onChange={(e) => setSeoData({ ...seoData, canonicalUrl: e.target.value })}
              className="w-full px-3.5 py-2 text-xs bg-white border border-slate-300 rounded-xl font-mono text-slate-900 focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setSeoData({ ...seoData, canonicalUrl: window.location.origin })}
              className="px-3 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-bold rounded-xl shrink-0 transition-colors"
              title="Gunakan domain yang saat ini aktif di browser"
            >
              Gunakan Domain Ini
            </button>
          </div>
          <p className="text-[11px] text-slate-600 mt-1.5">
            URL ini digunakan oleh generator <code>sitemap.xml</code> dan <code>rss.xml</code> untuk mengindeks seluruh halaman website di Google Search Console.
          </p>
        </div>
      </div>

      {/* SECTION 3: 3 MAIN GOOGLE WEBMASTER INTEGRATION CARDS */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <span>3. Integrasi Kode Webmaster (GSC, Analytics, AdSense)</span>
          </h3>
          <p className="text-xs text-slate-500">
            Cukup salin token atau ID dari panel Google, sistem akan menginjeksi kodenya secara otomatis ke seluruh halaman.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 1. Google Search Console */}
          <div className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                  <Search className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900">Google Search Console</h4>
                  <span className="text-[10px] text-slate-400">Verifikasi Kepemilikan</span>
                </div>
              </div>
              {integrations.googleSearchConsole ? (
                <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Aktif
                </span>
              ) : (
                <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  Belum Ada
                </span>
              )}
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                Kode Meta / Verification Token:
              </label>
              <input
                type="text"
                placeholder='contoh: abc123xyz atau <meta name="google-site-verification"...'
                value={integrations.googleSearchConsole}
                onChange={(e) => setIntegrations({ ...integrations, googleSearchConsole: e.target.value })}
                className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-900"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Ambil di GSC &rarr; Settings &rarr; Ownership Verification &rarr; HTML Tag.
              </p>
            </div>
          </div>

          {/* 2. Google Analytics (GA4) */}
          <div className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                  <BarChart3 className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900">Google Analytics 4</h4>
                  <span className="text-[10px] text-slate-400">Pantau Pengunjung Live</span>
                </div>
              </div>
              {integrations.googleAnalytics ? (
                <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Aktif
                </span>
              ) : (
                <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  Belum Ada
                </span>
              )}
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                Measurement ID (G-XXXXXXXXXX):
              </label>
              <input
                type="text"
                placeholder="G-ABC123XYZ"
                value={integrations.googleAnalytics}
                onChange={(e) => setIntegrations({ ...integrations, googleAnalytics: e.target.value })}
                className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-900"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Script <code>gtag.js</code> resmi Google akan langsung diinjeksikan otomatis ke seluruh halaman.
              </p>
            </div>
          </div>

          {/* 3. Google AdSense */}
          <div className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold">
                  <DollarSign className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900">Google AdSense</h4>
                  <span className="text-[10px] text-slate-400">Monetisasi Iklan Otomatis</span>
                </div>
              </div>
              {integrations.googleAdsense ? (
                <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Aktif
                </span>
              ) : (
                <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  Belum Ada
                </span>
              )}
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                Publisher ID (ca-pub-XXXXXXXXXX):
              </label>
              <input
                type="text"
                placeholder="ca-pub-1234567890123456"
                value={integrations.googleAdsense}
                onChange={(e) => setIntegrations({ ...integrations, googleAdsense: e.target.value })}
                className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-300 rounded-xl font-mono text-slate-900"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                Script <code>adsbygoogle.js</code> diinjeksi dengan mode Auto Ads siap tayang.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SAVE BUTTON AT BOTTOM */}
      <div className="flex flex-col items-end gap-2 pt-2 border-t border-slate-200">
        <button
          id="btn-save-seo-bottom"
          disabled={isSaving}
          onClick={handleSave}
          className="px-8 py-3 rounded-2xl text-white font-bold text-sm shadow-md transition-transform hover:scale-[1.02] disabled:opacity-50 flex items-center gap-2"
          style={{ backgroundColor: primaryColor }}
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Sedang Menyimpan Pengaturan...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span>Simpan Semua Pengaturan SEO & Integrasi Google</span>
            </>
          )}
        </button>
      </div>

      {/* SECTION 4: GOOGLE DISCOVER & FAST INDEXING FEATURES */}
      <div className="p-5 sm:p-6 bg-linear-to-br from-emerald-950 via-slate-900 to-slate-950 text-white rounded-3xl shadow-lg space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-white flex items-center gap-2">
                <span>Google Discover & Indexing Accelerator</span>
                <span className="bg-emerald-500/20 text-emerald-300 text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-emerald-400/30">
                  Auto-Optimized
                </span>
              </h3>
              <p className="text-xs text-slate-300">
                Sistem ini sudah diprogram memenuhi 100% persyaratan teknis algoritma Google Discover.
              </p>
            </div>
          </div>

          <button
            disabled={isPinging}
            onClick={handlePingGoogle}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl flex items-center gap-1.5 shadow-md transition-all shrink-0"
          >
            <Zap className="w-4 h-4 fill-slate-950 text-slate-950" />
            <span>{isPinging ? 'Sedang Mengirim Ping...' : '🚀 Ping Google & Bing Indexer Sekarang'}</span>
          </button>
        </div>

        {pingResult && (
          <div className="p-3 bg-emerald-950/80 border border-emerald-500/40 rounded-xl text-xs text-emerald-200 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{pingResult}</span>
          </div>
        )}

        {/* 4 Pillars of Google Discover in this app */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2">
          <div className="p-3.5 bg-white/5 rounded-2xl border border-white/10">
            <div className="flex items-center gap-1.5 font-bold text-xs text-emerald-300 mb-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Max Image Preview</span>
            </div>
            <p className="text-[11px] text-slate-300 leading-relaxed">
              Meta tag <code>max-image-preview:large</code> aktif di setiap halaman artikel untuk memicu visual kartu besar di feed Google Discover.
            </p>
          </div>

          <div className="p-3.5 bg-white/5 rounded-2xl border border-white/10">
            <div className="flex items-center gap-1.5 font-bold text-xs text-emerald-300 mb-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>NewsArticle Schema</span>
            </div>
            <p className="text-[11px] text-slate-300 leading-relaxed">
              Schema.org JSON-LD <code>NewsArticle</code> tersemat lengkap dengan tanggal publikasi, penulis, dan gambar 1200px.
            </p>
          </div>

          <div className="p-3.5 bg-white/5 rounded-2xl border border-white/10">
            <div className="flex items-center gap-1.5 font-bold text-xs text-emerald-300 mb-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Sitemap & RSS 2.0</span>
            </div>
            <p className="text-[11px] text-slate-300 leading-relaxed">
              Umpan <code>/rss.xml</code> dan <code>/sitemap.xml</code> yang otomatis terupdate seketika artikel baru Anda publish.
            </p>
          </div>

          <div className="p-3.5 bg-white/5 rounded-2xl border border-white/10">
            <div className="flex items-center gap-1.5 font-bold text-xs text-emerald-300 mb-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Format Gambar WebP</span>
            </div>
            <p className="text-[11px] text-slate-300 leading-relaxed">
              Kompresi otomatis WebP membuat waktu muat halaman (PageSpeed) sangat tinggi, faktor penting ranking SEO Google.
            </p>
          </div>
        </div>
      </div>

      {/* SECTION 5: QUICK INDEXER LINKS FOR GSC */}
      <div className="p-5 bg-slate-50 rounded-3xl border border-slate-200 space-y-3">
        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
          Link Endpoint Untuk Didaftarkan ke Google Search Console:
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3.5 bg-white rounded-2xl border border-slate-200 flex items-center justify-between shadow-2xs">
            <div className="overflow-hidden">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <FileCode2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>XML Sitemap URL</span>
              </span>
              <span className="text-[11px] text-slate-400 font-mono block truncate mt-0.5">
                {originUrl}/sitemap.xml
              </span>
            </div>
            <button
              onClick={() => copyToClipboard(`${originUrl}/sitemap.xml`, 'Sitemap')}
              className="p-2 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl shrink-0 transition-colors"
              title="Salin Link"
            >
              {copiedUrl === 'Sitemap' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>

          <div className="p-3.5 bg-white rounded-2xl border border-slate-200 flex items-center justify-between shadow-2xs">
            <div className="overflow-hidden">
              <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                <Rss className="w-4 h-4 text-amber-500 shrink-0" />
                <span>RSS 2.0 Google News Feed</span>
              </span>
              <span className="text-[11px] text-slate-400 font-mono block truncate mt-0.5">
                {originUrl}/rss.xml
              </span>
            </div>
            <button
              onClick={() => copyToClipboard(`${originUrl}/rss.xml`, 'RSS Feed')}
              className="p-2 text-slate-500 hover:text-amber-700 hover:bg-amber-50 rounded-xl shrink-0 transition-colors"
              title="Salin Link"
            >
              {copiedUrl === 'RSS Feed' ? <Check className="w-4 h-4 text-amber-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
